import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useEffect, useRef } from 'react';
import type { ElementType } from 'react';
import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { TONE_TO_CHIP_COLOR } from 'src/pages/instance-detail-page/entity-status-tone';

import { i18n } from './TopBarSearch.i18n';
import { useStyles } from './TopBarSearch.styles';
import type {
    TopBarSearchEntityType,
    TopBarSearchGroup,
    TopBarSearchResult,
} from './use-top-bar-search';
import { MIN_SEARCH_LENGTH } from './use-top-bar-search';

const GROUP_LABELS: Record<TopBarSearchEntityType, MessageDescriptor> = {
    instance: i18n.groupInstances,
    addon: i18n.groupSubscriptions,
    workspace: i18n.groupWorkspaces,
    dataset: i18n.groupDatasets,
    user: i18n.groupUsers,
    supportPackage: i18n.groupSupportPackages,
};

const GROUP_ICONS: Record<TopBarSearchEntityType, ElementType> = {
    instance: DnsOutlinedIcon,
    addon: CardMembershipOutlinedIcon,
    workspace: FolderOpenOutlinedIcon,
    dataset: DatasetOutlinedIcon,
    user: PersonOutlineIcon,
    supportPackage: Inventory2OutlinedIcon,
};

const SKELETON_ROW_KEYS = ['skeleton-1', 'skeleton-2', 'skeleton-3'];

const SkeletonRows = ({ classes }: { classes: ReturnType<typeof useStyles>['classes'] }) => (
    <>
        {SKELETON_ROW_KEYS.map((key) => (
            <Box key={key} className={classes.skeletonRow}>
                <Skeleton variant="circular" width={32} height={32} />
                <Box className={classes.skeletonText}>
                    <Skeleton variant="text" width="60%" height={16} />
                    <Skeleton variant="text" width="40%" height={12} />
                </Box>
                <Skeleton variant="rounded" width={48} height={18} />
            </Box>
        ))}
    </>
);

export interface SearchResultListProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    input: string;
    groups: TopBarSearchGroup[];
    indexByKey: Map<string, number>;
    activeIndex: number;
    isFetching: boolean;
    hasQuery: boolean;
    totalResults: number;
    onSelectResult: (result: TopBarSearchResult) => void;
    onActiveIndexChange: (index: number) => void;
}

export const SearchResultList = ({
    open,
    anchorEl,
    input,
    groups,
    indexByKey,
    activeIndex,
    isFetching,
    hasQuery,
    totalResults,
    onSelectResult,
    onActiveIndexChange,
}: SearchResultListProps) => {
    const intl = useIntl();
    const { classes, cx } = useStyles();
    const activeRowRef = useRef<HTMLLIElement | null>(null);

    const trimmedInput = input.trim();
    const hasShortInput = trimmedInput.length > 0 && trimmedInput.length < MIN_SEARCH_LENGTH;
    const safeActiveIndex = totalResults > 0 ? activeIndex % totalResults : -1;

    const showNoResults = hasQuery && !isFetching && totalResults === 0;
    const showSkeletons = isFetching && totalResults === 0;
    const showRefetchBar = isFetching && totalResults > 0;

    // Scroll the highlighted row into view when navigating by keyboard.
    useEffect(() => {
        activeRowRef.current?.scrollIntoView({ block: 'nearest' });
    }, [safeActiveIndex]);

    const getAvatarClass = (type: TopBarSearchEntityType, isSelected: boolean) => {
        if (isSelected) return cx(classes.resultAvatar, classes.resultAvatarSelected);
        const typeClass: Record<TopBarSearchEntityType, string> = {
            instance: classes.resultAvatarInstance,
            addon: classes.resultAvatarAddon,
            workspace: classes.resultAvatarWorkspace,
            dataset: classes.resultAvatarDataset,
            user: classes.resultAvatarUser,
            supportPackage: classes.resultAvatarSupportPackage,
        };
        return cx(classes.resultAvatar, typeClass[type]);
    };

    const renderAvatar = (result: TopBarSearchResult, isSelected: boolean) => {
        const Icon = GROUP_ICONS[result.type];
        if (result.type === 'user' && result.avatarLabel) {
            return (
                <Avatar className={getAvatarClass(result.type, isSelected)}>
                    {result.avatarLabel}
                </Avatar>
            );
        }
        return (
            <Avatar className={getAvatarClass(result.type, isSelected)}>
                <Icon sx={{ fontSize: '1rem' }} />
            </Avatar>
        );
    };

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            className={classes.dropdown}
        >
            <Paper className={classes.paper} automation-id="top-bar-search-results">
                {showRefetchBar && <LinearProgress className={classes.refetchBar} />}

                {hasShortInput && (
                    <Box className={classes.promptRow}>
                        <KeyboardIcon sx={{ fontSize: '1rem' }} />
                        <Typography variant="caption">
                            {intl.formatMessage(i18n.typePrompt, {
                                min: MIN_SEARCH_LENGTH,
                            })}
                        </Typography>
                    </Box>
                )}

                {showSkeletons && <SkeletonRows classes={classes} />}

                {showNoResults && (
                    <Box className={classes.emptyState} automation-id="top-bar-search-no-results">
                        <SearchOffOutlinedIcon className={classes.emptyIcon} />
                        <Typography variant="body2" fontWeight={500}>
                            {intl.formatMessage(i18n.noResults)}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                            {intl.formatMessage(i18n.noResultsHint)}
                        </Typography>
                    </Box>
                )}

                {groups.map((group, groupIndex) => {
                    const Icon = GROUP_ICONS[group.type];
                    return (
                        <Box key={group.type}>
                            {groupIndex > 0 && <Divider className={classes.groupDivider} />}
                            <MenuList disablePadding>
                                <ListSubheader
                                    disableGutters
                                    className={classes.groupHeaderRow}
                                    automation-id={`top-bar-search-group-${group.type}`}
                                >
                                    <Icon className={classes.groupIcon} />
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        letterSpacing="0.06em"
                                        textTransform="uppercase"
                                    >
                                        {intl.formatMessage(GROUP_LABELS[group.type])}
                                    </Typography>
                                    <span className={classes.groupCount}>
                                        {group.results.length}
                                    </span>
                                </ListSubheader>
                                {group.results.length === 0 ? (
                                    <Typography
                                        className={classes.noResultsInSection}
                                        component="div"
                                    >
                                        {intl.formatMessage(i18n.noResultsInSection)}
                                    </Typography>
                                ) : (
                                    group.results.map((result) => {
                                        const resultIndex = indexByKey.get(result.key) ?? -1;
                                        const isSelected = resultIndex === safeActiveIndex;
                                        const hasChips =
                                            result.status ||
                                            (result.badges && result.badges.length > 0);
                                        return (
                                            <MenuItem
                                                key={result.key}
                                                ref={isSelected ? activeRowRef : null}
                                                selected={isSelected}
                                                className={classes.resultRow}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    onSelectResult(result);
                                                }}
                                                onMouseEnter={() =>
                                                    onActiveIndexChange(resultIndex)
                                                }
                                            >
                                                <ListItemAvatar sx={{ minWidth: 'auto' }}>
                                                    {renderAvatar(result, isSelected)}
                                                </ListItemAvatar>
                                                <ListItemText
                                                    className={classes.resultText}
                                                    primary={result.primary}
                                                    secondary={
                                                        <>
                                                            {result.secondary && (
                                                                <span
                                                                    className={
                                                                        classes.resultSecondary
                                                                    }
                                                                >
                                                                    {result.secondary}
                                                                </span>
                                                            )}
                                                            {result.tertiary && (
                                                                <span
                                                                    className={
                                                                        classes.resultTertiary
                                                                    }
                                                                >
                                                                    {result.tertiary}
                                                                </span>
                                                            )}
                                                            {hasChips && (
                                                                <span
                                                                    className={
                                                                        classes.resultChipsRow
                                                                    }
                                                                >
                                                                    {result.status && (
                                                                        <Chip
                                                                            label={
                                                                                result.status.label
                                                                            }
                                                                            size="small"
                                                                            color={
                                                                                TONE_TO_CHIP_COLOR[
                                                                                    result.status
                                                                                        .tone
                                                                                ]
                                                                            }
                                                                            className={
                                                                                classes.stateChip
                                                                            }
                                                                        />
                                                                    )}
                                                                    {result.badges?.map((badge) => (
                                                                        <Chip
                                                                            key={badge}
                                                                            label={badge}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            className={
                                                                                classes.badgeChip
                                                                            }
                                                                        />
                                                                    ))}
                                                                </span>
                                                            )}
                                                        </>
                                                    }
                                                    slotProps={{
                                                        primary: {
                                                            className: classes.resultPrimary,
                                                        },
                                                        secondary: {
                                                            component: 'div',
                                                            className:
                                                                classes.resultSecondaryContainer,
                                                        } as object,
                                                    }}
                                                />
                                                <Box className={classes.resultMeta}>
                                                    <ChevronRightIcon
                                                        className={classes.visitIcon}
                                                    />
                                                </Box>
                                            </MenuItem>
                                        );
                                    })
                                )}
                                {group.hasMore && (
                                    <Typography
                                        className={classes.moreMatchesRow}
                                        component="div"
                                        automation-id={`top-bar-search-more-${group.type}`}
                                    >
                                        {intl.formatMessage(i18n.moreMatches)}
                                    </Typography>
                                )}
                            </MenuList>
                        </Box>
                    );
                })}
            </Paper>
        </Popper>
    );
};
